import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { getAiAssistantSettings, getExistingExternalConversationMuteStats } from '../services/companyService';
import type { AiAssistantSettings, ExternalConversationAiMuteStats } from '../types/Company';
import { AssistantChannelExistingMutePanel } from './AssistantChannelExistingMutePanel';
import { AssistantChannelFutureMutePanel } from './AssistantChannelFutureMutePanel';
import styles from './AssistantAiMuteAdvancedSettingsModal.module.css';

interface AssistantAiMuteAdvancedSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNotice: (message: string) => void;
  onError: (message: string) => void;
}

const emptyFutureSettings: Pick<
  AiAssistantSettings,
  | 'isExternalInboundAiAssistantEnabled'
  | 'isExternalInboundAiAssistantEnabledOverridden'
  | 'externalInboundAiAssistantChannelSettings'
> = {
  isExternalInboundAiAssistantEnabled: false,
  isExternalInboundAiAssistantEnabledOverridden: false,
  externalInboundAiAssistantChannelSettings: {},
};

const emptyMuteStats: ExternalConversationAiMuteStats = {
  totalUnmutedCount: 0,
  unmutedCountByChannel: {},
};

export const AssistantAiMuteAdvancedSettingsModal: React.FC<AssistantAiMuteAdvancedSettingsModalProps> = ({
  isOpen,
  onClose,
  onNotice,
  onError,
}) => {
  const { t } = useTranslation(['companies', 'common']);
  const [loading, setLoading] = React.useState(false);
  const [localError, setLocalError] = React.useState<string | null>(null);
  const [futureSettings, setFutureSettings] = React.useState(emptyFutureSettings);
  const [muteStats, setMuteStats] = React.useState<ExternalConversationAiMuteStats>(emptyMuteStats);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, loading, onClose]);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLocalError(null);

    void (async () => {
      try {
        const [settings, existingMuteStats] = await Promise.all([
          getAiAssistantSettings(),
          getExistingExternalConversationMuteStats(),
        ]);
        if (cancelled) {
          return;
        }

        setFutureSettings({
          isExternalInboundAiAssistantEnabled: settings.isExternalInboundAiAssistantEnabled,
          isExternalInboundAiAssistantEnabledOverridden: settings.isExternalInboundAiAssistantEnabledOverridden,
          externalInboundAiAssistantChannelSettings: settings.externalInboundAiAssistantChannelSettings,
        });
        setMuteStats(existingMuteStats);
      } catch (error) {
        if (!cancelled) {
          setLocalError(error instanceof Error ? error.message : t('failedToLoadAssistantSettings'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, t]);

  if (!isOpen) {
    return null;
  }

  const handleNotice = (message: string) => {
    setLocalError(null);
    onNotice(message);
  };

  const handleError = (message: string) => {
    const nextError = message || null;
    setLocalError(nextError);
    if (nextError) {
      onError(nextError);
    }
  };

  return createPortal(
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="assistant-mute-advanced-modal-title"
      onClick={() => {
        if (!loading) {
          onClose();
        }
      }}
    >
      <div
        className={styles.modalCard}
        onClick={event => event.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h3 id="assistant-mute-advanced-modal-title" className={styles.modalTitle}>
            {t('assistantMuteAdvancedSettingsTitle')}
          </h3>
          <button
            type="button"
            className={styles.modalCloseBtn}
            aria-label={t('close', { ns: 'common' })}
            disabled={loading}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          <p className={styles.intro}>{t('assistantMuteAdvancedSettingsDescription')}</p>
          {localError ? <div className="error">{localError}</div> : null}

          {loading ? (
            <div className={styles.loading}>{t('loadingAssistantSettings')}</div>
          ) : (
            <>
              <AssistantChannelFutureMutePanel
                settings={futureSettings}
                onSettingsChange={setFutureSettings}
                onNotice={handleNotice}
                onError={handleError}
              />
              <AssistantChannelExistingMutePanel
                stats={muteStats}
                onStatsChange={setMuteStats}
                onNotice={handleNotice}
                onError={handleError}
              />
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
            {t('close', { ns: 'common' })}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AssistantAiMuteAdvancedSettingsModal;
